import React, { useState, useEffect, ReactElement } from 'react';
import {
  Box,
  Text,
  Alert,
  AlertIcon,
  AlertText,
  InfoIcon,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import MermaidDiagram from './MermaidDiagram';
import log from '../../../../common/utils/logger';

interface FlowchartErrorBoundaryProps {
  children: ReactElement;
}

/**
 * FlowchartErrorBoundary component catches errors in MermaidDiagram
 * and displays a fallback UI
 */
const FlowchartErrorBoundary: React.FC<FlowchartErrorBoundaryProps> = ({ children }) => {
  const [hasError, setHasError] = useState(false);
  const { t } = useTranslation();

  // Reset error state if children change
  useEffect(() => {
    setHasError(false);
  }, [children]);

  // Handler for errors from MermaidDiagram
  const handleError = (error: string) => {
    log.error('Mermaid diagram error:', error);
    setHasError(true);
  };

  if (hasError) {
    return (
      <Box p="$4">
        <Alert action="error" variant="solid">
          <AlertIcon as={InfoIcon} mr="$2" />
          <AlertText>
            {t(
              'lecturer.moduleEditor.taskEditor.taskEvalForm.errors.diagramError',
              'The reasoning diagram could not be rendered due to parsing errors.',
            )}
          </AlertText>
        </Alert>
      </Box>
    );
  }

  // Clone the children to add the error handler
  return React.cloneElement(children as React.ReactElement<{ onError: (error: string) => void }>, {
    onError: handleError,
  });
};

interface FlowchartSectionProps {
  flowchartCode?: string;
  height?: number;
}

/**
 * FlowchartSection component displays the mermaid diagram showing the AI reasoning
 * for generating the rubric
 */
const FlowchartSection: React.FC<FlowchartSectionProps> = ({
  flowchartCode,
  height = 300,
}) => {
  const { t } = useTranslation();

  if (!flowchartCode) {
    return null;
  }

  return (
    <Box
      mb="$4"
      borderWidth={1}
      borderColor="$borderLight"
      borderRadius="$md"
      overflow="hidden"
      bg="$bgApp"
    >
      <Box bg="$bgSubtle" px="$3" py="$2" borderBottomWidth={1} borderBottomColor="$borderLight">
        <Text
          size="xs"
          fontWeight="bold"
          color="$textHint"
          textTransform="uppercase"
          letterSpacing="$md"
        >
          {t('lecturer.moduleEditor.taskEditor.taskEvalForm.labels.reasoningFlowchart')}
        </Text>
      </Box>
      <Box p="$2">
        <FlowchartErrorBoundary>
          <MermaidDiagram code={flowchartCode} height={height} horizontal zoomLevel={0.8} />
        </FlowchartErrorBoundary>
      </Box>
    </Box>
  );
};

export default FlowchartSection;
